<template>
  <div class="info-page">
    <div class="info-hero">
      <div class="info-hero__inner">
        <p class="info-eyebrow">Legal</p>
        <h1 class="info-h1">Cookie<br><em>Policy</em></h1>
        <p class="info-lead">
          This policy explains what cookies we use, why we use them, and how you can control them.
          Last updated: <strong>1 January 2025</strong>.
        </p>
      </div>
    </div>

    <div class="info-body legal-body">

      <section class="legal-section">
        <h2 class="legal-h2">What are cookies?</h2>
        <p>Cookies are small text files stored on your device when you visit a website. They help websites remember information about your visit — such as your login session or preferences — so the site works correctly on your next visit.</p>
        <p>We also use similar technologies such as localStorage and sessionStorage in your browser to store learning progress locally and reduce unnecessary server requests.</p>
      </section>

      <section class="legal-section">
        <h2 class="legal-h2">Cookies we use</h2>

        <div class="cookie-table-wrapper">
          <table class="cookie-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Purpose</th>
                <th>Duration</th>
                <th>Can I opt out?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span class="cookie-badge cookie-badge--essential">Essential</span></td>
                <td>Maintain your login session and keep you signed in across pages.</td>
                <td>Session / 30 days</td>
                <td>No — required for the Service to function.</td>
              </tr>
              <tr>
                <td><span class="cookie-badge cookie-badge--functional">Functional</span></td>
                <td>Remember your preferences (e.g., theme, active student) so you don't have to set them each visit.</td>
                <td>1 year</td>
                <td>Yes — clearing browser storage will reset preferences.</td>
              </tr>
              <tr>
                <td><span class="cookie-badge cookie-badge--analytics">Analytics</span></td>
                <td>Understand how the platform is used (page views, feature usage) to improve the product. Data is aggregated and anonymised; no individual users are identified.</td>
                <td>Up to 2 years</td>
                <td>Yes — see "Managing cookies" below.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="legal-section">
        <h2 class="legal-h2">Third-party services</h2>
        <p>We use the following third-party services that may set their own cookies or access local storage:</p>
        <ul class="legal-list">
          <li><strong>Stripe</strong> — Payment processing. Stripe may set cookies to prevent fraud and enable secure checkout. These are governed by <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
          <li><strong>AWS (Amazon Web Services)</strong> — Our backend infrastructure. No cookies are set directly by AWS on your browser.</li>
        </ul>
        <p>We do not use advertising cookies, and we do not sell data to any third parties.</p>
      </section>

      <section class="legal-section">
        <h2 class="legal-h2">Local browser storage</h2>
        <p>In addition to cookies, we use your browser's <strong>localStorage</strong> and <strong>sessionStorage</strong> to store your learning progress locally. This allows the app to work faster and to queue progress updates when you are offline.</p>
        <p>This data is stored only on your device and is not shared with third parties. You can clear it at any time using your browser's settings or developer tools.</p>
      </section>

      <section class="legal-section">
        <h2 class="legal-h2">Managing cookies</h2>
        <p>You can control and delete cookies through your browser settings. Please be aware that disabling essential cookies may prevent parts of the Service from working correctly.</p>
        <p>For guidance on managing cookies in your browser, visit:</p>
        <ul class="legal-list">
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
          <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
          <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
          <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
        </ul>
        <p>You can also opt out of analytics tracking by emailing us at <a href="mailto:privacy@mathselevenplus.co.uk">privacy@mathselevenplus.co.uk</a> with the subject "Analytics opt-out".</p>
      </section>

      <section class="legal-section">
        <h2 class="legal-h2">Changes to this policy</h2>
        <p>We may update this Cookie Policy from time to time. We will notify you of any significant changes by posting a notice on our website or by email. Your continued use of the Service after any change constitutes acceptance of the revised policy.</p>
      </section>

      <section class="legal-section">
        <h2 class="legal-h2">Contact us</h2>
        <p>If you have questions about our use of cookies, please contact us:</p>
        <address class="legal-address">
          Maths Eleven Plus Ltd — Privacy Team<br>
          Email: <a href="mailto:privacy@mathselevenplus.co.uk">privacy@mathselevenplus.co.uk</a><br>
          Or use our <RouterLink to="/info/contact">contact form</RouterLink>.
        </address>
      </section>

    </div>
  </div>
</template>

<script setup>
import { usePageMeta } from '@/composables/usePageMeta';
usePageMeta({ title: 'Cookie Policy — Maths Eleven Plus', description: 'Learn about the cookies and browser storage used by Maths Eleven Plus and how to manage them.' });
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Jost:wght@400;500;600;700&display=swap');

.info-page { font-family: 'Jost', sans-serif; }
.info-hero { background: #0D1628; padding: 5rem 2rem 4rem; }
.info-hero__inner { max-width: 52rem; margin: 0 auto; }
.info-eyebrow { font-size: 0.8rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #B8902A; margin-bottom: 1rem; }
.info-h1 { font-family: 'Cormorant Garamond', serif; font-size: clamp(2.25rem, 5vw, 3.25rem); font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 1.25rem; }
.info-h1 em { color: #B8902A; font-style: italic; }
.info-lead { font-size: 1.05rem; color: rgba(255,255,255,0.65); max-width: 44rem; line-height: 1.7; }

.info-body { max-width: 52rem; margin: 0 auto; padding: 3rem 2rem 4rem; }
.legal-section { margin-bottom: 2.5rem; }
.legal-h2 { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; font-weight: 700; color: #0D1628; margin-bottom: 0.875rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }
.legal-section p { font-size: 0.9rem; line-height: 1.8; color: #374151; margin-bottom: 0.75rem; }
.legal-list { margin: 0.5rem 0 0.75rem 1.5rem; display: flex; flex-direction: column; gap: 0.4rem; }
.legal-list li { font-size: 0.9rem; line-height: 1.7; color: #374151; }
.legal-list a { color: #B8902A; }
.legal-section a { color: #B8902A; }
.legal-address { font-style: normal; font-size: 0.9rem; line-height: 1.8; color: #374151; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 1rem 1.25rem; margin-top: 0.5rem; }
.legal-address a { color: #B8902A; }

/* Cookie table */
.cookie-table-wrapper { overflow-x: auto; margin: 1rem 0; border-radius: 0.75rem; border: 1px solid #e5e7eb; }
.cookie-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.cookie-table th { background: #f9fafb; padding: 0.75rem 1rem; text-align: left; font-size: 0.775rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; border-bottom: 1px solid #e5e7eb; white-space: nowrap; }
.cookie-table td { padding: 0.875rem 1rem; color: #374151; line-height: 1.6; vertical-align: top; border-bottom: 1px solid #f3f4f6; }
.cookie-table tr:last-child td { border-bottom: none; }

.cookie-badge { display: inline-block; padding: 0.2rem 0.6rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 700; white-space: nowrap; }
.cookie-badge--essential { background: #fef2f2; color: #dc2626; }
.cookie-badge--functional { background: #eff6ff; color: #2563eb; }
.cookie-badge--analytics { background: #f0fdf4; color: #16a34a; }
</style>
